import { ADMIN_KEY, apiFetch, loadSession } from "./session";
import { normalizeBlueprint, saveFlowBlueprint } from "./flowBlueprint";
import { emptyRevisions, loadRevisions, saveRevisions } from "./labStore";

/** Admin token: the current admin session, or the last admin login on this browser. */
export function adminToken() {
  const session = loadSession();
  if (session?.role === "admin" || session?.integrator) return session.token;
  return localStorage.getItem(ADMIN_KEY) || "";
}

export const forgetAdmin = () => localStorage.removeItem(ADMIN_KEY);

const adminFetch = (path, token, options = {}) =>
  apiFetch(path, token, { ...options, keepSession: true }).catch((problem) => {
    if (problem.status === 401 || problem.status === 403) forgetAdmin();
    throw problem;
  });

/** Load the admin draft from the server, then save every simulator edit back (debounced). */
export function startLabSync(token, onStatus) {
  let timer = null;
  let ready = false;
  const state = {};
  const push = (done = "Corrections enregistrées sur le serveur") => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      adminFetch("/api/lab/draft", token, { method: "PUT", body: JSON.stringify(state) })
        .then(() => onStatus?.(done))
        .catch((problem) => onStatus?.(problem.message || "Enregistrement impossible"));
    }, 1200);
  };
  const onRevisions = (event) => {
    state.revisions = event.detail;
    if (ready) push();
  };
  const onBlueprint = (event) => {
    state.blueprint = event.detail;
    if (ready) push();
  };
  window.addEventListener("sp-lab-revisions", onRevisions);
  window.addEventListener("sp-lab-blueprint", onBlueprint);
  adminFetch("/api/lab/draft", token)
    .then((draft) => {
      const local = loadRevisions();
      const server = { ...emptyRevisions(), ...(draft.revisions || {}) };
      const merged = {
        ...server,
        overrides: { ...server.overrides, ...local.overrides },
        promptOverrides: { ...server.promptOverrides, ...local.promptOverrides },
        integrations: [...new Map([...server.integrations, ...local.integrations].map((row) => [JSON.stringify(row), row])).values()],
        comments: { ...local.comments, ...server.comments },
      };
      const changed = (field) => Object.keys(local[field]).filter((key) => local[field][key] !== server[field][key]).length;
      const fromBrowser = changed("overrides") + changed("promptOverrides");
      saveRevisions(merged);
      if (draft.blueprint) saveFlowBlueprint(normalizeBlueprint(draft.blueprint));
      ready = true;
      const merging = `${fromBrowser} correction(s) de ce navigateur ajoutée(s) au serveur`;
      if (fromBrowser) push(merging);
      onStatus?.(
        fromBrowser ? merging : draft.updatedAt ? "Corrections chargées depuis le serveur" : "Aucune correction sur le serveur"
      );
    })
    .catch(() => onStatus?.("Serveur indisponible : corrections locales seulement"))
    .finally(() => {
      ready = true;
    });
  return () => {
    clearTimeout(timer);
    window.removeEventListener("sp-lab-revisions", onRevisions);
    window.removeEventListener("sp-lab-blueprint", onBlueprint);
  };
}

export const saveDraft = (token, revisions) =>
  adminFetch("/api/lab/draft", token, { method: "PUT", body: JSON.stringify({ revisions }) });

export const publishLab = (token) => adminFetch("/api/lab/publish", token, { method: "POST", body: "{}" });

export async function loadPublished() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const response = await fetch(`${base}/api/lab/published`);
  if (!response.ok) return {};
  const data = await response.json();
  return data.overrides || {};
}
